<script setup lang="ts">
import { QuestionFilled } from '@element-plus/icons-vue';
import { usePvpCardGameStore } from '../services/gameService';
import { usePvpCardProfileStore } from '../services/gameProfile';
import { computed, ref } from 'vue';
import type { PveStage } from '@/types/pvp-card';
import { dayjs } from 'element-plus';
import { debounce } from 'lodash';
import SoulCurrency from './SoulCurrency.vue'
import { formatNumberWithWan } from '@/utils';

const pvpCardGameStore = usePvpCardGameStore()
const pvpCardProfileStore = usePvpCardProfileStore()

const mainChallengeStage = computed(() => {
    return pvpCardGameStore.allStages.map((stage) => {
        const passedStages = stage.stages.reduce((acc, cur) => {
            if (pvpCardProfileStore.profile.passedStages.includes(cur.id)) {
                acc.push(cur)
            }
            return acc
        }, [] as PveStage[])
        const unPassedStages = stage.stages.filter((stage) => !passedStages.includes(stage))

        const currentStage = unPassedStages.length ? unPassedStages[0] : stage.stages[stage.stages.length - 1]
        const currentRewards = {
            ...unPassedStages.length ? currentStage.firstReward : currentStage.rewards,
            cardsDetail: ''
        }
        currentRewards.cardsDetail = currentRewards.cards.map((item) => {
            const detail = pvpCardGameStore.getDetailById(item[0])
            return `<span>${item[1]}</span><span class="${detail.grade}">${detail.name}</span>`
        }).join('')
        return {
            name: stage.name,
            currentStage,
            currentRewards,
            passedStages,
            stages: stage.stages,
            unPassed: !!unPassedStages.length,
        }
    })
})
const startPveBattle = debounce((stage: PveStage) => {
    if (pvpCardProfileStore.profile.stamina < 10) {
        ElMessage.error("⚡体力不足")
        return
    }
    pvpCardGameStore.startPveBattle(stage.id)
}, 1000, { leading: true })

function sweepPve() {
    if (pvpCardProfileStore.profile.stamina < 10) {
        ElMessage.error("⚡体力不足")
        return
    }
    const passedStageIds = new Set(pvpCardProfileStore.profile.passedStages);

    let bestStage = null;
    let bestValue = -1;
    let _coin = 0
    let _diamond = 0
    for (const chapter of pvpCardGameStore.allStages) {
        for (const stage of chapter.stages) {
            if (!passedStageIds.has(stage.id)) continue;
            const { coin = 0, diamond = 0 } = stage.rewards;
            const value = coin + diamond * 50; // 自定义权重
            if (value > bestValue) {
                bestValue = value;
                bestStage = stage;
                _coin = coin
                _diamond = diamond
            }
        }
    }
    if (!bestStage) return;
    const amount = Math.max(Math.floor(pvpCardProfileStore.profile.stamina / 10), 1)
    const staminaCost = amount * 10
    if (pvpCardProfileStore.profile.stamina < staminaCost) {
        ElMessage.error("⚡体力不足")
        return
    }
    ElMessageBox.confirm(`本次扫荡预计消耗${staminaCost}⚡,获得${_coin ? `${_coin * amount}魂尘` : ''}${_diamond ? `${_diamond * amount}魂晶` : ''},确认扫荡吗？`, '提示', {
        type: 'warning',
        appendTo: 'body',
        confirmButtonClass: 'is-plain',
    })
        .then(() => {
            pvpCardGameStore.sweepPve(bestStage.id, amount)
        })
        .catch(() => { })

}

</script>
<template>
    <div class="dungeon-page">
        <slot name="top"></slot>
        <div class="dungeon-list">
            <div class="dungeon affix">
                <div class="title">
                    <h2 class="unique">主线副本</h2>
                    <el-button v-show="pvpCardProfileStore.profile.passedStages.length" type="primary" plain
                        style="height: 25px; margin-left: 10px" @click="sweepPve()">一键扫荡</el-button>
                    <div class="stamina">
                        <span>⚡</span>

                        <span :class="pvpCardProfileStore.profile.stamina >= 10 ? 'crt' : 'shrink'">{{
                            pvpCardProfileStore.profile.stamina }}</span>
                        <span>/240</span>
                    </div>
                    <div class="help-info" style="margin-left: 5px">
                        <el-popover placement="top" width="100%" trigger="click">
                            <template #reference>
                                <el-icon size="20">
                                    <QuestionFilled />
                                </el-icon>
                            </template>
                            <div class="affix">
                                <h4>主线副本说明</h4>
                                <p>1. 主线副本分为多个主题副本,每个主题副本分为多个难度关卡，需依次通关</p>
                                <p>2. <span class="unique">首次通关</span>可以获取大量奖励，快速养成卡组，重复通关仅获取少量奖励</p>
                                <p>3. 每次挑战消耗10⚡，⚡为<span class="shrink">0</span>时将无法挑战</p>
                                <p>4. 每<span class="info">6分钟</span>恢复<span class="grow">1</span>点⚡</p>
                            </div>
                        </el-popover>
                    </div>
                </div>
                <div class="border-wrap main-challenge" v-for="(item, index) of mainChallengeStage" :key="item.name">
                    <div class="left" style="width: 100%">
                        <h4>
                            <div>
                                <span :class="`stage-${index + 1}`"> {{ item.name }}</span>
                                <span class="progress level-desc">({{
                                    `${item.passedStages.length}/${item.stages.length}`
                                    }})</span>
                            </div>
                            <el-button size="small" type="success" plain @click="startPveBattle(item.currentStage)">
                                <div class="affix">
                                    挑战<span class="shrink" style="margin-left: 4px;">消耗10⚡</span>
                                </div>
                            </el-button>

                        </h4>
                        <p class="level-name">
                            当前关卡：{{ item.currentStage.name }}
                        </p>
                        <p class="rewards">
                            <span>
                                {{ item.unPassed ? '首通奖励：' : '通关奖励：' }}
                            </span>
                            <template v-if="item.currentRewards.coin">
                                <span>
                                    <span>{{ formatNumberWithWan(item.currentRewards.coin) }}
                                    </span>
                                    <SoulCurrency size="12px" type="dust" :value="item.currentRewards.coin" />
                                </span>
                            </template>
                            <template v-if="item.currentRewards.diamond">
                                <span>
                                    <span>{{ formatNumberWithWan(item.currentRewards.diamond) }}
                                    </span>
                                    <SoulCurrency size="12px" type="quartz" :value="item.currentRewards.diamond" />
                                </span>
                            </template>
                            <template v-if="item.currentRewards.cards.length">
                                <span v-html="item.currentRewards.cardsDetail"> </span>
                            </template>
                        </p>
                        <p class="level-desc">{{ item.currentStage.description }}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<style lang="scss" scoped>
.dungeon-page {
    height: 100%;
    position: relative;
    padding-top: 40px;

    .title {
        display: flex;
        align-items: center;
        margin: 0 0 10px 10px;

        h2 {
            text-align: left;
            font-size: 22px;
            margin: 0;
        }
    }

    .dungeon-list {
        height: 100%;
        overflow: auto;

        .dungeon {
            font-size: 14px;
        }
    }

    .border-wrap {
        border: 1px solid #333;
    }

    .stamina {
        font-size: 12px;
        display: flex;
        align-items: center;
        margin-left: 8px;

    }

    .main-challenge {
        display: flex;
        background-color: #1a1a1a;
        color: #cfd3dc;

        h4 {
            justify-content: space-between;

            .progress {
                font-weight: normal;
                font-size: 14px;
                margin-left: 4px;
            }
        }

        .stage-1 {
            color: #4caf50;
        }

        /* 幽暗沼泽 - 绿 */
        .stage-2 {
            color: #2196f3;
        }

        /* 腐化林地 - 蓝 */
        .stage-3 {
            color: #ff9800;
        }

        /* 重装堡垒 - 橙 */
        .stage-4 {
            color: #f44336;
        }

        /* 熔岩裂谷 - 红 */
        .stage-5 {
            color: #9c27b0;
        }

        /* 暗影峡谷 - 紫 */
        // .level-name {
        // color: #cfd3dc;
        // }

        .rewards {
            font-size: 12px;
            flex: 1;

            &>span:not(:first-child) {
                margin-right: 8px;
            }
        }

        .level-desc {
            font-size: 12px;
            color: #aaa;
        }
    }
}
</style>